/**
 * Auto-fills the OTP field using the Web OTP API (`navigator.credentials.get`
 * with `otp.transport`), so compatible browsers (mainly Chrome on Android)
 * complete the code as soon as the SMS arrives, without user interaction.
 *
 * Safe by design for unsupported browsers and failure cases:
 *  - Feature detection (`'OTPCredential' in window`) guards the whole flow,
 *    so nothing runs on browsers without Web OTP support (e.g. Safari,
 *    Firefox, desktop Chrome).
 *  - Any rejection (expired, cancelled by the user, denied permission, or
 *    thrown by the API) is swallowed silently: no console errors, no UI
 *    impact. The field keeps working through manual entry / paste in every
 *    case, exactly as before this hook existed.
 *  - The pending request is aborted on unmount or when `enabled` turns off,
 *    to avoid resolving into a setter of an unmounted view.
 *
 * @param {(code: string) => void} setOtpCode - Setter for the OTP value.
 * @param {boolean} [enabled] - Whether to listen for an incoming OTP (e.g. only for the SMS delivery method, not WhatsApp).
 */
import { useEffect } from 'react';

export const isWebOtpSupported = () =>
  typeof window !== 'undefined' && 'OTPCredential' in window && !!navigator.credentials?.get;

export const useWebOtp = (setOtpCode, enabled = true) => {
  useEffect(() => {
    if (!enabled || !isWebOtpSupported()) return undefined;

    const abortController = new AbortController();

    try {
      navigator.credentials
        .get({ otp: { transport: ['sms'] }, signal: abortController.signal })
        .then((otpCredential) => {
          if (otpCredential?.code) setOtpCode(otpCredential.code);
        })
        .catch(() => {
          // Expired, cancelled or rejected: manual entry remains the fallback.
        });
    } catch (e) {
      // Defensive: some browsers report support but throw synchronously.
    }

    return () => abortController.abort();
  }, [enabled, setOtpCode]);
};
